import React, { FunctionComponent } from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import CalendarIcon from '@uniiverse/library/assets/icons/calendar.svg'
import MarkerIcon from '@uniiverse/library/assets/icons/marker.svg'
import { formattedDateTimeRange } from 'src/lib/formatter'
import { Price } from 'src/components/calendar/BuyTickets/Price'
import { Social } from 'src/components/calendar/Social'
import { TicketButton } from 'src/components/calendar/BuyTickets/TicketButton'
import { LoadingContent } from 'src/components/loading-content/LoadingContent'
import { LoadingPlaceholder } from 'src/components/loading-content/LoadingPlaceholder'
import { guessZone } from 'src/lib/display-date'

const style = require('./TicketWidget.css')
import locale from '../locale'

interface Props {
  eventData: any,
  baseUrl: string,
  expanded: boolean,
  loading: boolean,
  listingStatus: string,
  ticketButtonText: string,
  isTimedEntry: boolean,
  viewer: any,
}

const Placeholder = () => (
  <LoadingPlaceholder modifierClass={style.placeholder}>
    <LoadingPlaceholder modifierClass={style.price}>
      <LoadingContent width="40px" height="18px" borderRadius="rounder" />
      <LoadingContent width="112px" height="18px" borderRadius="rounder" />
    </LoadingPlaceholder>

    <LoadingPlaceholder modifierClass={style.content}>
      <LoadingContent height="33px" borderRadius="sharper" />
    </LoadingPlaceholder>
  </LoadingPlaceholder>
)

const TicketWidgetComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  eventData,
  baseUrl,
  expanded,
  loading,
  listingStatus,
  ticketButtonText,
  isTimedEntry,
  intl,
  viewer,
}) => {

  const getEventTime = () => {
    let startAt: Date, endAt: Date, tz = null

    if (eventData.virtual) {
      startAt = new Date(1000 * eventData.allTimeSlots[0].startStamp)
      endAt = new Date(1000 * eventData.allTimeSlots[0].endStamp)
      tz = guessZone(startAt)
    } else {
      startAt = eventData.allTimeSlots[0].startAt
      endAt = eventData.allTimeSlots[0].endAt
    }

    if (eventData.timedEntry) {
      return intl.formatMessage(locale.seeDates)
    }

    return formattedDateTimeRange({
      startAt,
      endAt,
      intl,
      totalCount: eventData.timeSlots.totalCount,
      tz,
    })
  }

  if (loading) return <Placeholder />
  // TODO: returning null if there are no timeslots? Make state for "noAvailableTimes"
  if (eventData.allTimeSlots.length === 0) return null;

  const showPriceText = (
    (eventData.minPrice !== null) || (eventData.maxPrice !== null)) && (listingStatus === 'availableStandard'
  )

  const location = eventData.virtual ? intl.formatMessage(locale.virtualEvent) : eventData.address;
  const showSocial = eventData.privacy === 'PUBLIC' || (eventData.privacy === 'PRIVATE' && eventData.socialButtons === true)

  return (
    <div className={style.container}>
      <div className={style.price}>
        <div>
          {showPriceText && intl.formatMessage(locale.price)}
        </div>
        <Price
          minPrice={eventData.minPrice}
          maxPrice={eventData.maxPrice}
          currency={eventData.transactionCurrency}
          listingStatus={listingStatus}
        />
      </div>

      <div className={style.content}>
        <div className={`${style.info} ${expanded ? '' : style.empty}`}>
          <div className={style.title}>{eventData.title}</div>
          {!eventData.hiddenDate &&
            <div className={style.time}>
              <CalendarIcon className={style.time__icon} />
              {getEventTime()}
            </div>
          }
          <div className={style.location}>
            <MarkerIcon className={style.location__icon} />
            {location}
          </div>
        </div>
        <TicketButton
          listingStatus={listingStatus}
          ticketButtonText={ticketButtonText}
          isTimedEntry={isTimedEntry}
          eventId={eventData.id}
          baseUrl={baseUrl}
          hostId={eventData.user.id}
          viewer={viewer}
        />
      </div>
      <Social hasBorder eventData={eventData} loading={loading} showSocial={showSocial}  />
    </div>
  )
}

export const TicketWidget = injectIntl(TicketWidgetComponent)
